/**
 * Servicio para gestión de licencias y datos del dashboard
 * Este archivo maneja la obtención y procesamiento de información consolidada
 * Se utiliza para alimentar los componentes del dashboard
 * 
 * ═══════════════════════════════════════════════════════════════════════
 * 📋 INSTRUCCIONES PARA INTEGRACIÓN CON BACKEND
 * ═══════════════════════════════════════════════════════════════════════
 * 
 * ENDPOINTS REQUERIDOS:
 * ---------------------
 * 1. GET /api/licenses/summary - Resumen de licencias
 * 2. GET /api/candidates/stats - Estadísticas de candidatos
 * 3. GET /api/candidates/job-fit - Distribución por ajuste al cargo
 * 
 * HEADERS NECESARIOS:
 * -------------------
 * - Authorization: Bearer {token}
 * - Content-Type: application/json
 * 
 * CONFIGURACIÓN:
 * --------------
 * - URL del API: Variable de entorno REACT_APP_API_URL
 * - Token: Se obtiene de localStorage.getItem('token')
 * 
 * CAMBIOS PARA PRODUCCIÓN:
 * ------------------------
 * 1. En cada función (getLicensesSummary, getCandidatesStats, getJobFitStats):
 *    - ELIMINAR: return getMockXXX();
 *    - ⚠️ DESCOMENTAR: throw error;
 * 
 * 2. En ResumenGeneral/index.js (catch de loadDashboardData):
 *    - ELIMINAR: Las 3 líneas que cargan datos mock
 *    - USAR: Solo setError con mensaje al usuario
 * 
 * VER DOCUMENTACIÓN COMPLETA EN: EXPLICACION_RESUMEN_GENERAL.md
 * ═══════════════════════════════════════════════════════════════════════
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

/**
 * Obtiene el resumen consolidado de licencias
 * @returns {Promise<Object>} Datos consolidados de licencias
 */
export const getLicensesSummary = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/licenses/summary`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Error al obtener resumen de licencias');
    }

    return await response.json();
  } catch (error) {
    console.error('Error en getLicensesSummary:', error);
    // ⚠️ DESARROLLO: Retorna datos mock. En producción eliminar la siguiente  línea
    return getMockLicensesSummary();
    // ✅ PRODUCCIÓN: Descomentar la siguiente linea para lanzar error real y mostrar mensaje al usuario
    // throw error;
  }
};


/**
 * Obtiene estadísticas de candidatos
 * @returns {Promise<Object>} Estadísticas de candidatos por estado
 */
export const getCandidatesStats = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/candidates/stats`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Error al obtener estadísticas de candidatos');
    }

    return await response.json();
  } catch (error) {
    console.error('Error en getCandidatesStats:', error);
    // ⚠️ DESARROLLO: Retorna datos mock. En producción eliminar esta línea
    return getMockCandidatesStats();
    // ✅ PRODUCCIÓN: Descomentar para lanzar error real y mostrar mensaje al usuario
    // throw error;
  }
};

/**
 * Obtiene estadísticas de ajuste al cargo
 * @returns {Promise<Object>} Distribución de candidatos por nivel de ajuste
 */
export const getJobFitStats = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/candidates/job-fit`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Error al obtener estadísticas de ajuste al cargo');
    }

    return await response.json();
  } catch (error) {
    console.error('Error en getJobFitStats:', error);
    // ⚠️ DESARROLLO: Retorna datos mock. En producción eliminar esta línea
    return getMockJobFitStats();
    // ✅ PRODUCCIÓN: Descomentar para lanzar error real y mostrar mensaje al usuario
    // throw error;
  }
};

/**
 * Obtiene todos los datos consolidados del dashboard
 * @returns {Promise<Object>} Objeto con todas las estadísticas
 */
export const getDashboardData = async () => {
  try {
    const [licenses, candidates, jobFit] = await Promise.all([
      getLicensesSummary(),
      getCandidatesStats(),
      getJobFitStats(),
    ]);

    return {
      licenses,
      candidates,
      jobFit,
      lastUpdated: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Error en getDashboardData:', error);
    throw error;
  }
};

/**
 * Verifica si se debe mostrar alerta de licencias bajas
 * @param {number} availableLicenses - Licencias disponibles
 * @returns {boolean} True si debe mostrar alerta
 */
export const shouldShowLicenseAlert = (availableLicenses) => {
  return availableLicenses <= 5;
};

/**
 * Calcula el porcentaje de uso de licencias
 * @param {number} used - Licencias usadas
 * @param {number} total - Total de licencias
 * @returns {number} Porcentaje de uso (0-100)
 */
export const calculateLicenseUsagePercentage = (used, total) => {
  if (total === 0) return 0;
  return Math.round((used / total) * 100);
};

/**
 * Determina el color de la barra de progreso según el porcentaje
 * @param {number} percentage - Porcentaje de uso
 * @returns {string} Color para la barra de progreso
 */
export const getLicenseUsageColor = (percentage) => {
  if (percentage >= 90) return 'error';
  if (percentage >= 75) return 'warning';
  if (percentage >= 50) return 'info';
  return 'success';
};

// ============================================================================
// DATOS MOCK PARA DESARROLLO Y PRUEBAS
// ⚠️ EN PRODUCCIÓN: Estos datos NO se usarán si el backend funciona correctamente
// ============================================================================

/**
 * Datos de ejemplo para resumen de licencias
 * 
 * 📡 FORMATO ESPERADO DEL BACKEND - GET /api/licenses/summary
 * Debe retornar un objeto con esta estructura:
 * {
 *   "totalLicenses": number,      // Total de licencias contratadas
 *   "usedLicenses": number,        // Licencias actualmente en uso
 *   "availableLicenses": number,   // Licencias disponibles (totalLicenses - usedLicenses)
 *   "expiredLicenses": number,     // Licencias que ya expiraron (opcional)
 *   "expirationDate": "YYYY-MM-DD", // Fecha de expiración del plan
 *   "planType": string,            // Nombre del plan (ej: "Empresarial Pro")
 *   "lastPurchaseDate": "YYYY-MM-DD" // Fecha de última compra
 * }
 */
const getMockLicensesSummary = () => ({
  totalLicenses: 50,
  usedLicenses: 46,
  availableLicenses: 4,
  expiredLicenses: 3,
  expirationDate: '2025-12-31',
  planType: 'Empresarial Pro',
  lastPurchaseDate: '2025-01-15',
});

/**
 * Datos de ejemplo para estadísticas de candidatos
 * 
 * 📡 FORMATO ESPERADO DEL BACKEND - GET /api/candidates/stats
 * Debe retornar un objeto con esta estructura:
 * {
 *   "total": number,               // Total de candidatos registrados
 *   "evaluated": number,           // Candidatos con evaluación completada
 *   "inProgress": number,          // Candidatos con evaluación en progreso
 *   "discarded": number,           // Candidatos descartados
 *   "percentages": {               // Porcentajes calculados (opcional, se puede calcular en frontend)
 *     "evaluated": number,         // Porcentaje de evaluados
 *     "inProgress": number,        // Porcentaje en progreso
 *     "discarded": number          // Porcentaje descartados
 *   }
 * }
 */
const getMockCandidatesStats = () => ({
  total: 42,
  evaluated: 35,
  inProgress: 5,
  discarded: 2,
  percentages: {
    evaluated: 83,
    inProgress: 12,
    discarded: 5,
  },
});

/**
 * Datos de ejemplo para ajuste al cargo
 * 
 * 📡 FORMATO ESPERADO DEL BACKEND - GET /api/candidates/job-fit
 * Debe retornar un objeto con esta estructura:
 * {
 *   "high": number,                // Candidatos con alto ajuste al cargo
 *   "medium": number,              // Candidatos con ajuste medio
 *   "low": number,                 // Candidatos con bajo ajuste
 *   "total": number,               // Total de candidatos evaluados
 *   "percentages": {               // Porcentajes calculados (opcional)
 *     "high": number,              // Porcentaje de alto ajuste
 *     "medium": number,            // Porcentaje de ajuste medio
 *     "low": number                // Porcentaje de bajo ajuste
 *   }
 * }
 */
const getMockJobFitStats = () => ({
  high: 18,
  medium: 14,
  low: 10,
  total: 42,
  percentages: {
    high: 43,
    medium: 33,
    low: 24,
  },
});

/**
 * Formatea una fecha ISO a formato legible
 * @param {string} isoDate - Fecha en formato ISO
 * @returns {string} Fecha formateada
 */
export const formatDate = (isoDate) => {
  const date = new Date(isoDate);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Obtiene el estado de salud de las licencias
 * @param {number} availableLicenses - Licencias disponibles
 * @returns {Object} Estado con nivel y mensaje
 */
export const getLicenseHealthStatus = (availableLicenses) => {
  if (availableLicenses <= 5) {
    return {
      level: 'critical',
      message: 'Quedan muy pocas licencias disponibles',
      color: 'error',
    };
  }
  if (availableLicenses <= 10) {
    return {
      level: 'warning',
      message: 'Las licencias disponibles están por agotarse',
      color: 'warning',
    };
  }
  return {
    level: 'healthy',
    message: 'Tienes suficientes licencias disponibles',
    color: 'success',
  };
};

export default {
  getLicensesSummary,
  getCandidatesStats,
  getJobFitStats,
  getDashboardData,
  shouldShowLicenseAlert,
  calculateLicenseUsagePercentage,
  getLicenseUsageColor,
  formatDate,
  getLicenseHealthStatus,
};
